import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { fetchCategories } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import ImageInput from "@/components/shared/ImageInput";
import { useZipLookup } from "@/lib/useZipLookup";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const STATES = [
    "CDMX", "Jalisco", "Nuevo León", "Yucatán", "Oaxaca", "Puebla",
    "Querétaro", "Guanajuato", "Veracruz", "Quintana Roo", "Baja California",
    "Estado de México", "Michoacán", "Chihuahua", "Sonora", "Sinaloa",
];

export default function MerchantStoreEdit() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasStore, setHasStore] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [categories, setCategories] = useState([]);

    const [form, setForm] = useState({
        name: "",
        slug: "",
        shortDescription: "",
        description: "",
        logo: "",
        banner: "",
        city: "",
        state: "Jalisco",
        category: "moda",
        street: "",
        exteriorNumber: "",
        interiorNumber: "",
        neighborhood: "",
        postalCode: "",
        phone: "",
        satProductKey: "",
        satProductName: "",
        allowPickup: true,
    });

    useEffect(() => {
        fetchCategories()
            .then((cats) => setCategories(Array.isArray(cats) ? cats : []))
            .catch(() => setCategories([]));
    }, []);

    useEffect(() => {
        apiFetch.get("/merchant/store").then(({ data }) => {
            if (data) {
                setHasStore(true);
                const loc = data.location || {};
                setForm({
                    name: data.name || "",
                    slug: data.slug || "",
                    shortDescription: data.shortDescription || "",
                    description: data.description || "",
                    logo: data.logo || "",
                    banner: data.banner || "",
                    city: loc.city || "",
                    state: loc.state || "Jalisco",
                    category: data.category || "moda",
                    street: loc.street || "",
                    exteriorNumber: loc.exteriorNumber || "",
                    interiorNumber: loc.interiorNumber || "",
                    neighborhood: loc.neighborhood || "",
                    postalCode: loc.postalCode || "",
                    phone: loc.phone || "",
                    satProductKey: data.satProductKey || "",
                    satProductName: data.satProductName || "",
                    allowPickup: data.allowPickup !== false,
                });
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const { suggestions: zipSuggestions, lookup: lookupZip } = useZipLookup();

    const onZipChange = async (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 5);
        setForm((s) => ({ ...s, postalCode: v }));
        if (v.length === 5) {
            const first = await lookupZip(v);
            if (first) {
                // Autocompleta ciudad; el estado se queda en el selector (puede
                // no coincidir exacto con el nombre que devuelve PE).
                setForm((s) => ({ ...s, postalCode: v, city: first.city || first.municipality || s.city }));
            }
        }
    };

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Mínimo 2 caracteres.";
        if (form.shortDescription.trim().length < 4)
            e.shortDescription = "Cuenta brevemente qué ofreces.";
        if (form.description.trim().length < 10)
            e.description = "Mínimo 10 caracteres.";
        if (!form.logo) e.logo = "Sube o pega un logo.";
        if (form.city.trim().length < 2) e.city = "Ingresa tu ciudad.";
        if (form.postalCode && !/^[A-Za-z0-9 -]{3,10}$/.test(form.postalCode.trim())) {
            e.postalCode = "Código postal inválido.";
        }
        return e;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setSaving(true);
        setServerError(null);
        const payload = {
            name: form.name.trim(),
            slug: form.slug.trim() || undefined,
            shortDescription: form.shortDescription.trim(),
            description: form.description.trim(),
            logo: form.logo,
            banner: form.banner || form.logo,
            location: {
                city: form.city.trim(),
                state: form.state,
                street: form.street.trim() || null,
                exteriorNumber: form.exteriorNumber.trim() || null,
                interiorNumber: form.interiorNumber.trim() || null,
                neighborhood: form.neighborhood.trim() || null,
                postalCode: form.postalCode.trim() || null,
                phone: form.phone.trim() || null,
                country: "MX",
            },
            category: form.category,
            satProductKey: form.satProductKey.trim() || null,
            satProductName: form.satProductName.trim() || null,
            allowPickup: !!form.allowPickup,
        };
        try {
            if (hasStore) {
                await apiFetch.put("/merchant/store", payload);
                toast.success("Tienda actualizada");
            } else {
                await apiFetch.post("/merchant/store", payload);
                toast.success("¡Tienda creada!", {
                    description: "Ahora sube tu primer producto.",
                });
                navigate("/dashboard/onboarding");
                return;
            }
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "No se pudo guardar.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return <div className="h-64 rounded-xl bg-zinc-100 animate-pulse" />;

    return (
        <div data-testid="merchant-store-edit" className="space-y-6 max-w-3xl">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
                    {hasStore ? "Editar mi tienda" : "Crea tu tienda"}
                </h1>
                <p className="mt-2 text-sm text-zinc-500">
                    Esta información se muestra en tu vitrina pública.
                </p>
            </header>

            <form
                onSubmit={onSubmit}
                noValidate
                className="bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8 space-y-6"
            >
                <ImageInput
                    label="Logo de tu tienda *"
                    value={form.logo}
                    onChange={(v) => set("logo", v)}
                    aspect="aspect-square"
                    testid="store-logo-input"
                />
                {errors.logo && (
                    <p className="text-xs text-red-600 -mt-3">{errors.logo}</p>
                )}

                <ImageInput
                    label="Banner (opcional)"
                    value={form.banner}
                    onChange={(v) => set("banner", v)}
                    aspect="aspect-[16/6]"
                    testid="store-banner-input"
                />

                <Field label="Nombre de la tienda *" error={errors.name}>
                    <Input
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Mi tienda increíble"
                        data-testid="store-name-input"
                        className="h-11"
                    />
                </Field>

                <Field
                    label="Identificador (URL)"
                    error={errors.slug}
                    hint={
                        form.slug
                            ? `tiendify.io/${form.slug}`
                            : "Se genera automáticamente del nombre"
                    }
                >
                    <Input
                        value={form.slug}
                        onChange={(e) => set("slug", e.target.value.toLowerCase())}
                        placeholder="mi-tienda"
                        data-testid="store-slug-input"
                        className="h-11"
                    />
                </Field>

                <Field label="Descripción corta *" error={errors.shortDescription}>
                    <Input
                        value={form.shortDescription}
                        onChange={(e) => set("shortDescription", e.target.value)}
                        placeholder="Tejidos artesanales contemporáneos"
                        data-testid="store-short-desc-input"
                        className="h-11"
                        maxLength={200}
                    />
                </Field>

                <Field label="Descripción larga *" error={errors.description}>
                    <Textarea
                        value={form.description}
                        onChange={(e) => set("description", e.target.value)}
                        placeholder="Cuenta tu historia, qué materiales usas, dónde fabricas..."
                        data-testid="store-description-input"
                        rows={5}
                    />
                </Field>

                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Categoría principal *">
                        <Select
                            value={form.category}
                            onValueChange={(v) => set("category", v)}
                        >
                            <SelectTrigger
                                data-testid="store-category-select"
                                className="h-11"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => (
                                    <SelectItem key={c.slug} value={c.slug} className="capitalize">
                                        {c.name || c.slug}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Estado *">
                        <Select
                            value={form.state}
                            onValueChange={(v) => set("state", v)}
                        >
                            <SelectTrigger
                                data-testid="store-state-select"
                                className="h-11"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATES.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {s}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                </div>

                <Field label="Ciudad *" error={errors.city}>
                    <Input
                        value={form.city}
                        onChange={(e) => set("city", e.target.value)}
                        placeholder="Guadalajara"
                        data-testid="store-city-input"
                        className="h-11"
                    />
                </Field>

                <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-4">
                    <Label className="text-sm font-semibold text-zinc-900">
                        Dirección de origen del envío
                    </Label>
                    <p className="text-xs text-zinc-500 mt-1">
                        Desde dónde mandas tus productos. Es opcional para crear
                        la tienda, pero te la pediremos antes de generar tu primera
                        guía de envío.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                            <Field label="Calle">
                                <Input
                                    value={form.street}
                                    onChange={(e) => set("street", e.target.value)}
                                    placeholder="Av. Vallarta"
                                    data-testid="store-street-input"
                                    className="h-11"
                                />
                            </Field>
                        </div>
                        <Field label="No. exterior">
                            <Input
                                value={form.exteriorNumber}
                                onChange={(e) =>
                                    set("exteriorNumber", e.target.value)
                                }
                                placeholder="1234"
                                data-testid="store-ext-input"
                                className="h-11"
                            />
                        </Field>
                        <Field label="No. interior">
                            <Input
                                value={form.interiorNumber}
                                onChange={(e) =>
                                    set("interiorNumber", e.target.value)
                                }
                                placeholder="A"
                                data-testid="store-int-input"
                                className="h-11"
                            />
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label="Colonia">
                                <Input
                                    value={form.neighborhood}
                                    onChange={(e) =>
                                        set("neighborhood", e.target.value)
                                    }
                                    placeholder="Americana"
                                    data-testid="store-neighborhood-input"
                                    list="store-zip-neighborhoods"
                                    className="h-11"
                                />
                                <datalist id="store-zip-neighborhoods">
                                    {[...new Set(zipSuggestions.map((s) => s.neighborhood).filter(Boolean))].map((n) => (
                                        <option key={n} value={n} />
                                    ))}
                                </datalist>
                            </Field>
                        </div>
                        <Field
                            label="Código postal"
                            error={errors.postalCode}
                            hint="Al escribir 5 dígitos autocompletamos la ciudad."
                        >
                            <Input
                                value={form.postalCode}
                                onChange={onZipChange}
                                placeholder="44150"
                                data-testid="store-postal-input"
                                maxLength={5}
                                inputMode="numeric"
                                className="h-11"
                            />
                        </Field>
                        <Field label="Teléfono de contacto">
                            <Input
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                placeholder="33 1234 5678"
                                data-testid="store-phone-input"
                                maxLength={20}
                                className="h-11"
                            />
                        </Field>
                    </div>
                    <div className="mt-4">
                        <Label className="text-xs font-medium text-zinc-700">
                            Clave SAT del producto
                        </Label>
                        <p className="text-xs text-zinc-500 mt-1">
                            Se usa al generar las guías de envío. Escribe qué vendes
                            (mín. 3 letras) y elige la clave que mejor describa tus
                            productos.
                        </p>
                        <div className="mt-2">
                            <SatProductPicker
                                valueKey={form.satProductKey}
                                valueName={form.satProductName}
                                onPick={(item) => {
                                    set("satProductKey", item ? item.key : "");
                                    set("satProductName", item ? item.name : "");
                                }}
                            />
                        </div>
                    </div>

                    <label className="mt-4 flex items-start gap-2.5 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.allowPickup}
                            onChange={(e) => set("allowPickup", e.target.checked)}
                            data-testid="store-allow-pickup"
                            className="mt-0.5"
                        />
                        <span>
                            <span className="font-medium text-zinc-900">Permitir entrega acordada</span>
                            <span className="block text-xs text-zinc-500 mt-0.5">
                                El comprador podrá elegir "acordar la entrega contigo" en el checkout
                                (venta local: sin envío ni costo de envío). Desactívalo si solo vendes
                                con envío.
                            </span>
                        </span>
                    </label>
                </div>

                {serverError && (
                    <p
                        data-testid="store-edit-error"
                        className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                    >
                        {serverError}
                    </p>
                )}

                <div className="pt-2 flex gap-3">
                    <Button
                        type="submit"
                        disabled={saving}
                        data-testid="store-save-button"
                        className="bg-primary hover:bg-primary-hover text-primary-foreground"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-1" />
                        )}
                        {hasStore ? "Guardar cambios" : "Crear mi tienda"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, error, hint, children }) {
    return (
        <div>
            <Label className="text-xs font-medium text-zinc-700">{label}</Label>
            <div className="mt-1.5">{children}</div>
            {hint && !error && (
                <p className="mt-1 text-xs text-zinc-600">{hint}</p>
            )}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
}

function SatProductPicker({ valueKey, valueName, onPick }) {
    const [query, setQuery] = useState(valueName || "");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setQuery(valueName || "");
    }, [valueName]);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 3 || (valueName && q === valueName)) {
            setResults([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(() => {
            apiFetch
                .get("/merchant/sat-products", { params: { s: q } })
                .then(({ data }) => {
                    if (!cancelled) {
                        setResults(Array.isArray(data) ? data : []);
                        setOpen(true);
                    }
                })
                .catch(() => !cancelled && setResults([]))
                .finally(() => !cancelled && setLoading(false));
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [query, valueName]);

    return (
        <div className="relative">
            <Input
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    if (valueKey) onPick(null); // limpia la selección si edita
                }}
                onFocus={() => results.length && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Ej. ropa, café, joyería…"
                data-testid="store-sat-input"
                className="h-11"
            />
            {valueKey && (
                <p className="mt-1 text-xs text-emerald-700">
                    Seleccionado: <span className="font-mono">{valueKey}</span> · {valueName}
                </p>
            )}
            {open && (loading || results.length > 0) && (
                <div className="absolute z-20 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg max-h-64 overflow-y-auto">
                    {loading && (
                        <div className="px-3 py-2 text-xs text-zinc-400">Buscando…</div>
                    )}
                    {!loading &&
                        results.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onPick(r);
                                    setQuery(r.name);
                                    setOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-zinc-50 text-sm"
                                data-testid={`sat-option-${r.key}`}
                            >
                                <span className="font-mono text-xs text-zinc-500">{r.key}</span>{" "}
                                <span className="text-zinc-900">{r.name}</span>
                                {r.description && (
                                    <span className="block text-[11px] text-zinc-400">{r.description}</span>
                                )}
                            </button>
                        ))}
                    {!loading && results.length === 0 && (
                        <div className="px-3 py-2 text-xs text-zinc-400">Sin coincidencias.</div>
                    )}
                </div>
            )}
        </div>
    );
}
